import React from "react";
import {
  Book,
  Receipt,
  LogOut,
  LayoutDashboard,
  X,
  Menu,
  Users,
   Gift,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/think_cloudly_logo_transparent.png";

const Sidebar = ({ toggleSidebar, openProfileModal, isSidebarOpen }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const isAdmin = user?.role === "admin";

  //added
const canRefer = user?.allowed_referral === "yes"; 


  const linkClass = (path) =>
    `flex items-center w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
      currentPath === path
        ? "bg-gradient-to-r from-[#FC7A69]/20 to-[#FC7A69]/10 text-[#FC7A69] border-l-4 border-[#FC7A69] shadow-sm"
        : "text-gray-700 hover:bg-gray-50 hover:text-[#FC7A69] hover:translate-x-1"
    }`;

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 768) toggleSidebar();
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {!isSidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-all"
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
      )}

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={toggleSidebar}
        />
      )}

      {/* SIDEBAR CONTAINER */}
      <div
        className={`
          fixed md:static inset-y-0 left-0 z-40 
          w-72 md:w-64 lg:w-60
          bg-white/95 backdrop-blur-lg shadow-2xl md:shadow-lg 
          transform transition-transform duration-300 ease-in-out
          border-r border-gray-200/50
          flex flex-col
          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }
        `}
      >
        {/* HEADER (FIXED) */}
        <div className="flex-shrink-0 p-6 pt-8 md:pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm">
                <img
                  src={logo}
                  alt="Think Cloudly Logo"
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#221E45]">
                  Trainer Portal
                </h2>
                <p className="text-xs text-gray-500">
                  {isAdmin ? "Admin Dashboard" : "Manage your batches"}
                </p>
              </div>
            </div>

            {/* Close button (Mobile only) */}
            <button
              onClick={toggleSidebar}
              className="md:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* SCROLLABLE MENU SECTION */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-4">
          <nav className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-4">
              Main Menu
            </p>

            <button
              onClick={() => handleNavigation("/dashboard")}
              className={linkClass("/dashboard")}
            >
              <LayoutDashboard className="w-5 h-5 mr-3" />
              <span>Dashboard</span>
              {currentPath === "/dashboard" && (
                <div className="ml-auto w-2 h-2 bg-[#FC7A69] rounded-full"></div>
              )}
            </button>

            <button
              onClick={() => handleNavigation("/batches")}
              className={linkClass("/batches")}
            >
              <Book className="w-5 h-5 mr-3" />
              <span>All Batches</span>
              {currentPath === "/batches" && (
                <div className="ml-auto w-2 h-2 bg-[#FC7A69] rounded-full"></div>
              )}
            </button>

            <button
              onClick={() => handleNavigation("/all-invoices")}
              className={linkClass("/all-invoices")}
            >
              <Receipt className="w-5 h-5 mr-3" />
              <span>All Invoices</span>
              {currentPath === "/all-invoices" && (
                <div className="ml-auto w-2 h-2 bg-[#FC7A69] rounded-full"></div>
              )}
            </button>


{canRefer && (
  <button
    onClick={() => handleNavigation("/refer-and-earn")}
    className={linkClass("/refer-and-earn")}
  >
    <Gift className="w-5 h-5 mr-3" />
    <span>Refer & Earn</span>
    {currentPath === "/refer-and-earn" && (
      <div className="ml-auto w-2 h-2 bg-[#FC7A69] rounded-full"></div>
    )}
  </button>
  )} 


            {isAdmin && (
              <button
                onClick={() => handleNavigation("/instructors")}
                className={linkClass("/instructors")}
              >
                <Users className="w-5 h-5 mr-3" />
                <span>Instructors</span>
                {currentPath === "/instructors" && (
                  <div className="ml-auto w-2 h-2 bg-[#FC7A69] rounded-full"></div>
                )}
              </button>
            )}
          </nav>
        </div>

        {/* ACCOUNT + FOOTER SECTION (FIXED AT BOTTOM) */}
        <div className="flex-shrink-0 border-t border-gray-200 p-6">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-4">
              Account
            </p>

            {/* Edit Profile Button */}
            <button
              onClick={() => {
                openProfileModal();
                if (window.innerWidth < 768) toggleSidebar();
              }}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-700 
                hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
            >
              <div className="w-5 h-5 mr-3 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              </div>
              <span>Edit Profile</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 
                hover:bg-red-50 rounded-lg transition-all"
            >
              <LogOut className="w-5 h-5 mr-3" />
              <span>Logout</span>
            </button>
          </div>

          {/* FOOTER */}
          <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-200 mt-4">
            <p>© 2026 Trainer Portal</p>
            <p className="mt-1">Version 1.3</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
